import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { api } from "@/api/client";
import { queryKeys } from "@/api/queryKeys";
import type { DataResponse, StavZakazky } from "@/api/schemas";

/**
 * Objednávky — čo treba na zákazku kúpiť a čo už je zaobstarané.
 *
 * Súpis sa **dopočítava** z dielcov (rovnaký výpočet ako PDF „Súpis pre
 * objednávky"), uložený je len stav objednania. Preto sa po každej zmene
 * invaliduje aj prehľad zákaziek — pokrok „8/12" sa tým hýbe.
 */

export type StavPolozky =
  | "caka"
  | "rezervovane"
  | "objednane"
  | "prijate"
  | "zo_skladu";

export interface ObjednavkyRiadok {
  /** `null` = vlastná položka mimo cenníka (KOLO 211), stav cez `vlastna_kluc` */
  material_id: string | null;
  vlastna_kluc: string | null;
  kod: string;
  nazov: string;
  druh: string;
  mnozstvo: string;
  mj: string;
  poznamka_supisu: string;
  stav: StavPolozky;
  rezervoval: string | null;
  rezervovane_at: string | null;
  objednal: string | null;
  objednane_at: string | null;
  prijate_at: string | null;
  eshop: string | null;
  cislo_objednavky: string | null;
  dodanie_ocakavane: string | null;
  nahrada_material_id: string | null;
  nahrada_nazov: string | null;
  nahrada_text: string | null;
  poznamka: string | null;
  /** Kód u dodávateľa — do jeho eshopu a do zoznamu kódov ide toto */
  eshop_kod: string;
  eshop_url: string | null;
  /**
   * Názvy setov, z ktorých tento komponent vypadol pri rozpade (KOLO 103).
   * Prázdne pole = položka zo setu nepochádza. Komponent môže byť
   * v dvoch setoch naraz (podložka pri dvoch typoch závesu), preto zoznam.
   */
  zo_setu: string[];
}

export interface ObjednavkySekcia {
  kluc: string;
  label: string;
  riadky: ObjednavkyRiadok[];
}

export interface MimoSupisu {
  material_id: string | null;
  vlastna_kluc: string | null;
  kod: string | null;
  nazov: string | null;
  stav: StavPolozky;
  objednal: string | null;
  objednane_at: string | null;
  poznamka: string | null;
}

/** Varianta zákazky v riadku „Varianty:" (KOLO 206). */
export interface ObjednavkyVarianta {
  id: string;
  /** Ako na záložke — časť menom, „Výpočet A", výrobná menom */
  nazov: string;
  /** Číslo záložky — tá istá predpona ako v PDF (`1-A1`) */
  cislo: number;
  je_vyrobna: boolean;
  je_cast: boolean;
  vybrana: boolean;
}

export interface ObjednavkyPrehlad {
  zakazka_id: string;
  cislo_zakazky: string;
  /** Všetky aktívne varianty v poradí záložiek, vybrané s `vybrana` */
  varianty: ObjednavkyVarianta[];
  celkom: number;
  vyriesenych: number;
  sekcie: ObjednavkySekcia[];
  mimo_supisu: MimoSupisu[];
}

export interface ZakazkaPrehlad {
  zakazka_id: string;
  cislo_zakazky: string;
  nazov: string | null;
  stav: StavZakazky;
  termin_dodania: string | null;
  varianta_id: string | null;
  celkom: number;
  vyriesenych: number;
}

/**
 * Cesta položky pod `/zakazky/{id}/objednavky/` — materiál podľa id,
 * vlastná položka mimo cenníka podľa kľúča (KOLO 211).
 */
export function cestaPolozky(r: {
  material_id: string | null;
  vlastna_kluc: string | null;
}): string {
  return r.material_id ?? `vlastne/${r.vlastna_kluc ?? ""}`;
}

export interface PolozkaUpdate {
  stav: StavPolozky;
  eshop?: string | null;
  cislo_objednavky?: string | null;
  dodanie_ocakavane?: string | null;
  nahrada_material_id?: string | null;
  nahrada_text?: string | null;
  poznamka?: string | null;
}

/**
 * Zákazky, na ktorých sa objednáva — bez stavu tie, ktoré rozhodne server:
 * v príprave a vo výrobe (KOLO 204 — materiál sa objednáva počas prípravy).
 */
export function useObjednavkyPrehlad(
  stav?: string,
): UseQueryResult<DataResponse<ZakazkaPrehlad[]>> {
  return useQuery({
    queryKey: queryKeys.objednavky.prehlad(stav ?? "prijate"),
    queryFn: async () => {
      const { data } = await api.get<DataResponse<ZakazkaPrehlad[]>>(
        "/objednavky",
        { params: stav ? { stav } : {} },
      );
      return data;
    },
  });
}

/**
 * Položky jednej zákazky, zoskupené podľa dodávateľa. `varianty` — čo sa
 * nakupuje spolu (KOLO 206); bez nich rozhodne server (výrobná, inak
 * všetky časti, inak aktívny výpočet).
 */
export function useObjednavkyZakazky(
  zakazkaId: string | undefined,
  varianty?: string[],
): UseQueryResult<DataResponse<ObjednavkyPrehlad>> {
  const vyber = varianty?.length ? varianty.join(",") : "";
  return useQuery({
    queryKey: queryKeys.objednavky.zakazka(zakazkaId ?? "", vyber),
    queryFn: async () => {
      const { data } = await api.get<DataResponse<ObjednavkyPrehlad>>(
        `/zakazky/${zakazkaId}/objednavky`,
        { params: vyber ? { varianta_ids: vyber } : {} },
      );
      return data;
    },
    enabled: !!zakazkaId,
    // Pri prepnutí varianty ostane starý súpis, kým príde nový — bez bliknutia
    placeholderData: (pred) => pred,
  });
}

function useInvalidacia(): () => void {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: queryKeys.objednavky.all });
  };
}

/** Nastav stav jednej položky. */
export function useUlozPolozku(
  zakazkaId: string,
): UseMutationResult<
  DataResponse<ObjednavkyRiadok>,
  Error,
  { polozka: string; data: PolozkaUpdate }
> {
  const invaliduj = useInvalidacia();
  return useMutation({
    mutationFn: async ({ polozka, data }) => {
      const res = await api.put<DataResponse<ObjednavkyRiadok>>(
        `/zakazky/${zakazkaId}/objednavky/${polozka}`,
        data,
      );
      return res.data;
    },
    onSuccess: invaliduj,
  });
}

/** Vráť položku na „čaká“ — riadok na serveri zmizne. */
export function useVratPolozku(
  zakazkaId: string,
): UseMutationResult<void, Error, string> {
  const invaliduj = useInvalidacia();
  return useMutation({
    mutationFn: async (polozka: string) => {
      await api.delete(`/zakazky/${zakazkaId}/objednavky/${polozka}`);
    },
    onSuccess: invaliduj,
  });
}

/** Jeden stav pre celú sekciu dodávateľa — objednáva sa po košíkoch. */
export function useHromadne(
  zakazkaId: string,
): UseMutationResult<
  DataResponse<{ hotovo: number; poslane: number }>,
  Error,
  {
    material_ids: string[];
    /** Vlastné položky toho istého košíka (KOLO 211) */
    vlastne_kluce?: string[];
    stav: StavPolozky;
    eshop?: string | null;
    cislo_objednavky?: string | null;
  }
> {
  const invaliduj = useInvalidacia();
  return useMutation({
    mutationFn: async (payload) => {
      const res = await api.post<
        DataResponse<{ hotovo: number; poslane: number }>
      >(`/zakazky/${zakazkaId}/objednavky/hromadne`, payload);
      return res.data;
    },
    onSuccess: invaliduj,
  });
}
